import { create } from 'zustand';
import { http } from '@/api/http';
import { selectData, type SuccessResponseDTO } from '@/api/envelope';
import { ROUTE_ROLES, type RouteKey } from './rbac';
import { AppRole } from './roles';
import { getAccessToken } from './token-store';

interface EffectivePermissionsDTO {
  pageKeys?: string[] | null;
}

type Status = 'idle' | 'loading' | 'ready' | 'error';

interface PagePermissionsState {
  status: Status;
  pageKeys: ReadonlySet<string>;
  /** Load from API; on failure fall back to ROUTE_ROLES for the given user roles. */
  load: (userRoles: readonly AppRole[]) => Promise<void>;
  clear: () => void;
  canAccessPage: (pageKey: string) => boolean;
}

function allRouteKeysExceptSystemPermissions(): Set<string> {
  return new Set(
    (Object.keys(ROUTE_ROLES) as RouteKey[]).filter((k) => k !== 'system-permissions'),
  );
}

function fallbackKeys(userRoles: readonly AppRole[]): Set<string> {
  // Administrator — full access including System & Permissions.
  if (userRoles.includes(AppRole.Administrator)) {
    return new Set(Object.keys(ROUTE_ROLES));
  }
  // Legacy System Admin — full access except System & Permissions (Administrator-only).
  if (userRoles.includes(AppRole.SystemAdmin)) {
    return allRouteKeysExceptSystemPermissions();
  }
  const keys = new Set<string>();
  for (const [key, allowed] of Object.entries(ROUTE_ROLES) as [RouteKey, AppRole[]][]) {
    if (userRoles.some((r) => allowed.includes(r))) keys.add(key);
  }
  return keys;
}

export const usePagePermissionsStore = create<PagePermissionsState>((set, get) => ({
  status: 'idle',
  pageKeys: new Set(),
  clear: () => set({ status: 'idle', pageKeys: new Set() }),
  canAccessPage: (pageKey) => get().pageKeys.has(pageKey),
  load: async (userRoles) => {
    if (userRoles.includes(AppRole.Administrator)) {
      set({ status: 'ready', pageKeys: new Set(Object.keys(ROUTE_ROLES)) });
      return;
    }
    if (userRoles.includes(AppRole.SystemAdmin)) {
      set({ status: 'ready', pageKeys: allRouteKeysExceptSystemPermissions() });
      return;
    }
    if (!getAccessToken()) {
      set({ status: 'ready', pageKeys: fallbackKeys(userRoles) });
      return;
    }
    set({ status: 'loading' });
    try {
      const res = await http.get<SuccessResponseDTO<EffectivePermissionsDTO>>(
        '/api/auth/effective-permissions',
      );
      const data = selectData(res.data);
      const keys = new Set((data?.pageKeys ?? []).filter(Boolean));
      // If API returns empty unexpectedly, keep seed fallback so Marketer/etc. still work.
      set({
        status: 'ready',
        pageKeys: keys.size > 0 ? keys : fallbackKeys(userRoles),
      });
    } catch {
      set({ status: 'error', pageKeys: fallbackKeys(userRoles) });
    }
  },
}));

export function useCanAccessPage(pageKey: RouteKey | string): boolean {
  return usePagePermissionsStore((s) => s.pageKeys.has(pageKey));
}

export function usePagePermissionsReady(): boolean {
  const status = usePagePermissionsStore((s) => s.status);
  return status === 'ready' || status === 'error';
}
